// import React, { useContext, useEffect, useMemo, useState } from 'react';
// import { AuthContext } from '../../../context/AuthContext';
// import { AsyncState, ConfirmationDialog, StatusBadge } from '../components';
// import { normalizeAdminApiError } from '../errors';
// import { can } from '../permissions';
// import { useMobileAccount, useMobileAccountAction } from './hooks';
// import type { MobileAccountAction, MobileAccountActivationData } from './types';
// import './mobileAccountCard.css';

// const dates = (value: string | null | undefined) => value ? new Date(value).toLocaleString() : 'Not yet';

// const actionCopy: Record<Exclude<MobileAccountAction, 'create'>, { title: string; description: string; label: string; confirmation?: string; danger?: boolean }> = {
//     reset: { title: 'Reset mobile activation', description: 'This clears the current password, revokes active sessions, and creates a new one-time activation token.', label: 'Reset activation', confirmation: 'RESET ACTIVATION', danger: true },
//     enable: { title: 'Enable mobile account', description: 'The student will be allowed to sign in again after this change.', label: 'Enable account' },
//     disable: { title: 'Disable mobile account', description: 'The student will lose mobile access and every active session will be revoked.', label: 'Disable account', confirmation: 'DISABLE ACCOUNT', danger: true },
//     logout: { title: 'Log out every session', description: 'Every current student session will be revoked. The account itself remains enabled.', label: 'Log out all', confirmation: 'LOG OUT ALL', danger: true },
// };

// export function MobileAccountCard({ studentId }: { studentId: string }) {
//     const { user } = useContext(AuthContext);
//     const permitted = can(user?.role, 'mobile:manage');
//     const query = useMobileAccount(studentId, permitted);
//     const [action, setAction] = useState<Exclude<MobileAccountAction, 'create'> | null>(null);
//     const [activation, setActivation] = useState<MobileAccountActivationData | null>(null);
//     const [copied, setCopied] = useState(false);
//     const mutation = useMobileAccountAction(studentId, data => setActivation(data));
//     const error = query.error ? normalizeAdminApiError(query.error) : null;
//     const mutationError = mutation.error ? normalizeAdminApiError(mutation.error) : null;
//     const account = query.data?.data.account;
//     const missing = error?.status === 404;

//     useEffect(() => () => setActivation(null), []);
//     const rows = useMemo(() => account ? [
//         ['Login ID', account.loginId],
//         ['Last login', dates(account.lastLoginAt)],
//         ['Activated', dates(account.activatedAt)],
//         ['Disabled', account.disabledAt ? dates(account.disabledAt) : 'No'],
//         ['Password change required', account.mustChangePassword ? 'Yes' : 'No'],
//         ['Updated', dates(account.updatedAt)],
//     ] : [], [account]);

//     if (!permitted) return null;

//     const create = () => mutation.mutate({ action: 'create' });
//     const confirm = ({ reason }: { reason?: string }) => {
//         if (!action) return;
//         mutation.mutate({ action, reason });
//         setAction(null);
//     };
//     const copyToken = async () => {
//         if (!activation) return;
//         try {
//             if (!navigator.clipboard) return;
//             await navigator.clipboard.writeText(activation.activationToken);
//             setCopied(true);
//         } catch {
//             setCopied(false);
//         }
//     };

//     return (
//         <section className="mobile-account-card" aria-labelledby="mobile-account-title">
//             <div className="mobile-account-card__header">
//                 <div><p className="mobile-account-card__eyebrow">Student application</p><h2 id="mobile-account-title">Mobile Access</h2></div>
//                 {account && <StatusBadge status={account.status} />}
//             </div>

//             {missing ? (
//                 <div className="mobile-account-card__setup">
//                     <div><strong>No mobile account</strong><p>Create a pending account and issue a one-time activation token for this student.</p></div>
//                     <button className="ma-button ma-button--primary" type="button" onClick={create} disabled={mutation.isPending}>{mutation.isPending ? 'Creating…' : 'Create mobile account'}</button>
//                 </div>
//             ) : (
//                 <AsyncState loading={query.isLoading} error={error} onRetry={() => query.refetch()} empty={!account} emptyMessage="No account information was returned.">
//                     {account && <>
//                         <dl className="mobile-account-card__grid">{rows.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl>
//                         <div className="mobile-account-card__actions">
//                             {account.status === 'disabled' && <button className="ma-button ma-button--primary" type="button" onClick={() => setAction('enable')}>Enable</button>}
//                             {account.status !== 'disabled' && <button className="ma-button ma-button--danger" type="button" onClick={() => setAction('disable')}>Disable</button>}
//                             <button className="ma-button ma-button--secondary" type="button" onClick={() => setAction('logout')}>Log out all</button>
//                             <button className="ma-button ma-button--secondary" type="button" onClick={() => setAction('reset')}>Reset activation</button>
//                         </div>
//                     </>}
//                 </AsyncState>
//             )}

//             {mutationError && <div className="mobile-account-card__mutation-error" role="alert">{mutationError.message}{mutationError.requestId && <small> Request ID: {mutationError.requestId}</small>}</div>}

//             {activation && <div className="mobile-account-card__activation" role="status">
//                 <strong>One-time activation token</strong>
//                 <p>Share through the approved support channel. It will not be shown again after dismissal.</p>
//                 <code>{activation.activationToken}</code>
//                 <span>Expires {dates(activation.activationExpiresAt)}</span>
//                 <div><button type="button" className="ma-button ma-button--primary" onClick={copyToken}>{copied ? 'Copied' : 'Copy token'}</button><button type="button" className="ma-button ma-button--secondary" onClick={() => { setActivation(null); setCopied(false); }}>Dismiss permanently</button></div>
//             </div>}

//             {action && <ConfirmationDialog open title={actionCopy[action].title} description={actionCopy[action].description} confirmLabel={actionCopy[action].label} confirmationText={actionCopy[action].confirmation} requireReason danger={actionCopy[action].danger} busy={mutation.isPending} onCancel={() => setAction(null)} onConfirm={confirm} />}
//         </section>
//     );
// }

import React, { useContext, useEffect, useMemo, useState } from "react";

import {
  Clipboard,
  ClipboardCheck,
  Clock3,
  KeyRound,
  LogOut,
  RefreshCcw,
  ShieldCheck,
  ShieldOff,
  Smartphone,
  Trash2,
  TriangleAlert,
  UserRoundCheck,
} from "lucide-react";

import { AuthContext } from "../../../context/AuthContext";

import { AsyncState, ConfirmationDialog, StatusBadge } from "../components";

import { normalizeAdminApiError } from "../errors";

import { can } from "../permissions";

import { useMobileAccount, useMobileAccountAction } from "./hooks";

import type { MobileAccountAction, MobileAccountActivationData } from "./types";

/* =========================================================
   HELPERS
========================================================= */

const dates = (value: string | null | undefined) =>
  value ? new Date(value).toLocaleString() : "Not yet";

/* =========================================================
   ACTION COPY
========================================================= */

const actionCopy: Record<
  Exclude<MobileAccountAction, "create">,
  {
    title: string;
    description: string;
    label: string;
    confirmation?: string;
    danger?: boolean;
    reasonMinLength?: number;
  }
> = {
  activate: {
    title: "Create activated account",
    description: "Revokes all previous devices, sessions and OTPs. The account becomes active with password password123, without an OTP. On the next login, approve the student's device change manually. Ask the student to change this shared password afterward.",
    label: "Create activated account",
    confirmation: "CREATE ACTIVATED ACCOUNT",
    danger: true,
  },
  reset: {
    title: "Reset mobile activation",

    description:
      "This clears the current password, revokes active sessions, and creates a new one-time activation OTP.",

    label: "Reset activation",

    confirmation: "RESET ACTIVATION",

    danger: true,
  },

  enable: {
    title: "Enable mobile account",

    description:
      "The student will be allowed to sign in again after this change.",

    label: "Enable account",
  },

  disable: {
    title: "Disable mobile account",

    description:
      "The student will lose mobile access and every active session will be revoked.",

    label: "Disable account",

    confirmation: "DISABLE ACCOUNT",

    danger: true,
  },

  logout: {
    title: "Log out all and reset activation",

    description:
      "The current device and sessions will be revoked, the password will be cleared, and a new 72-hour activation OTP will be created.",

    label: "Log out all & create activation OTP",

    confirmation: "LOG OUT ALL",

    danger: true,
  },
  delete: {
    title: "Delete mobile account",
    description:
      "Last-resort recovery only. This permanently removes the student's mobile account after revoking its devices, sessions, pending approvals, activations, downloads, and access grants. The main student record and course enrollments are not deleted.",
    label: "Delete mobile account",
    confirmation: "DELETE MOBILE ACCOUNT",
    danger: true,
    reasonMinLength: 5,
  },
};

/* =========================================================
   INFO ICON
========================================================= */

function DetailIcon({ label }: { label: string }) {
  const className = "h-[16px] w-[16px]";

  if (label === "Login ID") {
    return <Smartphone className={className} strokeWidth={1.8} />;
  }

  if (label === "Last login") {
    return <Clock3 className={className} strokeWidth={1.8} />;
  }

  if (label === "Activated") {
    return <UserRoundCheck className={className} strokeWidth={1.8} />;
  }

  if (label === "Disabled") {
    return <ShieldOff className={className} strokeWidth={1.8} />;
  }

  if (label === "Password change required") {
    return <KeyRound className={className} strokeWidth={1.8} />;
  }

  return <RefreshCcw className={className} strokeWidth={1.8} />;
}

/* =========================================================
   COMPONENT
========================================================= */

export function MobileAccountCard({ studentId }: { studentId: string }) {
  const { user } = useContext(AuthContext);

  /* =====================================================
       PERMISSIONS
    ===================================================== */

  const permitted = can(user?.role, "mobile:manage");

  /* =====================================================
       QUERY
    ===================================================== */

  const query = useMobileAccount(studentId, permitted);

  /* =====================================================
       STATE
    ===================================================== */

  const [action, setAction] = useState<Exclude<
    MobileAccountAction,
    "create"
  > | null>(null);

  const [activation, setActivation] =
    useState<MobileAccountActivationData | null>(null);

  const [copied, setCopied] = useState(false);

  /* =====================================================
       MUTATION
    ===================================================== */

  const mutation = useMobileAccountAction(studentId, (data) =>
    setActivation(data),
  );

  /* =====================================================
       ERRORS
    ===================================================== */

  const error = query.error ? normalizeAdminApiError(query.error) : null;

  const mutationError = mutation.error
    ? normalizeAdminApiError(mutation.error)
    : null;

  /* =====================================================
       ACCOUNT
    ===================================================== */

  const deletionSucceeded =
    mutation.isSuccess && mutation.variables?.action === "delete";

  const account = deletionSucceeded ? undefined : query.data?.data.account;

  const missing = error?.status === 404 || deletionSucceeded;

  const activatedAccountButton = (
    <button
      type="button"
      disabled={mutation.isPending}
      onClick={() => setAction("activate")}
      className="inline-flex h-9 items-center justify-center gap-1.5 rounded-[9px] border border-emerald-200 bg-white px-3 text-[10px] font-semibold text-emerald-700 transition hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-emerald-200 disabled:cursor-not-allowed disabled:opacity-50"
    >
      <UserRoundCheck size={14} />
      Create activated account
    </button>
  );

  /* =====================================================
       CLEANUP
    ===================================================== */

  useEffect(() => () => setActivation(null), []);

  /* =====================================================
       SUMMARY ROWS
    ===================================================== */

  const rows = useMemo(
    () =>
      account
        ? [
            ["Login ID", account.loginId],

            ["Last login", dates(account.lastLoginAt)],

            ["Activated", dates(account.activatedAt)],

            ["Disabled", account.disabledAt ? dates(account.disabledAt) : "No"],

            [
              "Password change required",
              account.mustChangePassword ? "Yes" : "No",
            ],

            ["Updated", dates(account.updatedAt)],
          ]
        : [],
    [account],
  );

  /* =====================================================
       NO PERMISSION
    ===================================================== */

  if (!permitted) {
    return null;
  }

  /* =====================================================
       CREATE
    ===================================================== */

  const create = () =>
    mutation.mutate({
      action: "create",
    });

  /* =====================================================
       CONFIRM ACTION
    ===================================================== */

  const confirm = ({ reason }: { reason?: string }) => {
    if (!action) {
      return;
    }

    mutation.mutate({
      action,
      reason,
    });
    setActivation(null);

    setAction(null);
  };

  /* =====================================================
       COPY ACTIVATION TOKEN
    ===================================================== */

  const copyToken = async () => {
    if (!activation) {
      return;
    }

    try {
      if (!navigator.clipboard) {
        return;
      }

      await navigator.clipboard.writeText(activation.activationToken);

      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  /* =====================================================
       RENDER
    ===================================================== */

  return (
    <section
      aria-labelledby="mobile-account-title"
      className="
                overflow-hidden
                rounded-[16px]
                border
                border-slate-200
                bg-white
                shadow-[0_1px_3px_rgba(15,23,42,0.04)]
            "
    >
      {/* ============================================
                HEADER
            ============================================ */}

      <header
        className="
                    flex
                    items-start
                    justify-between
                    gap-4
                    border-b
                    border-slate-100
                    px-5
                    py-4

                    max-sm:px-4
                "
      >
        <div
          className="
                        flex
                        items-start
                        gap-3
                    "
        >
          <div
            className="
                            grid
                            h-9
                            w-9
                            shrink-0
                            place-items-center
                            rounded-[10px]
                            bg-slate-100
                            text-slate-600
                        "
          >
            <Smartphone size={18} strokeWidth={1.8} />
          </div>

          <div>
            <p
              className="
                                m-0
                                text-[9px]
                                font-bold
                                uppercase
                                tracking-[0.11em]
                                text-blue-600
                            "
            >
              Student application
            </p>

            <h2
              id="mobile-account-title"
              className="
                                mb-0
                                mt-1
                                text-[17px]
                                font-bold
                                tracking-[-0.02em]
                                text-slate-950
                            "
            >
              Mobile Access
            </h2>

            <p
              className="
                                mb-0
                                mt-1
                                text-[10px]
                                leading-4
                                text-slate-400
                            "
            >
              Manage the student's mobile login, activation, and active access.
            </p>
          </div>
        </div>

        {account && <StatusBadge status={account.status} />}
      </header>

      {/* ============================================
                BODY
            ============================================ */}

      <div
        className="
                    p-5
                    max-sm:p-4
                "
      >
        {/* ========================================
                    NO ACCOUNT
                ======================================== */}

        {mutation.isSuccess && mutation.variables?.action === "activate" && (
          <p role="status" className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3.5 text-[11px] leading-5 text-emerald-800">Account activated. Password: password123. Approve the device change after the student logs in, then ask them to change their password.</p>
        )}
        {missing ? (
          <div
            className="
                            flex
                            items-center
                            justify-between
                            gap-5
                            rounded-xl
                            border
                            border-slate-200
                            bg-slate-50
                            p-4

                            max-md:flex-col
                            max-md:items-stretch
                        "
          >
            <div
              className="
                                flex
                                min-w-0
                                items-start
                                gap-3
                            "
            >
              <div
                className="
                                    grid
                                    h-10
                                    w-10
                                    shrink-0
                                    place-items-center
                                    rounded-[10px]
                                    bg-white
                                    text-slate-500
                                    shadow-sm
                                "
              >
                <Smartphone size={18} strokeWidth={1.8} />
              </div>

              <div>
                <strong
                  className="
                                        block
                                        text-[13px]
                                        font-semibold
                                        text-slate-900
                                    "
                >
                  No mobile account
                </strong>

                <p
                  className="
                                        mb-0
                                        mt-1
                                        max-w-[620px]
                                        text-[10px]
                                        leading-5
                                        text-slate-500
                                    "
                >
                  Create a pending mobile account and issue a one-time
                  activation token for this student.
                </p>
              </div>
            </div>

            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 max-md:w-full max-md:flex-col max-md:items-stretch">
            {activatedAccountButton}
            <button
              type="button"
              onClick={create}
              disabled={mutation.isPending}
              className="
                                inline-flex
                                h-9
                                shrink-0
                                items-center
                                justify-center
                                gap-2
                                rounded-[9px]
                                border-0
                                bg-[#0d162a]
                                px-4
                                text-[11px]
                                font-semibold
                                text-white
                                transition
                                hover:bg-[#172239]
                                focus:outline-none
                                focus:ring-2
                                focus:ring-slate-300
                                disabled:cursor-not-allowed
                                disabled:opacity-50

                                max-md:w-full
                            "
            >
              {mutation.isPending ? (
                <>
                  <span
                    className="
                                            h-3.5
                                            w-3.5
                                            animate-spin
                                            rounded-full
                                            border-2
                                            border-white/30
                                            border-t-white
                                        "
                  />
                  Creating...
                </>
              ) : (
                <>
                  <UserRoundCheck size={14} />
                  Create mobile account
                </>
              )}
            </button>
            </div>
          </div>
        ) : (
          /* ====================================
                       ACCOUNT DETAILS
                    ==================================== */

          <AsyncState
            loading={query.isLoading}
            error={error}
            onRetry={() => query.refetch()}
            empty={!account}
            emptyMessage="No account information was returned."
          >
            {account && (
              <>
                {/* ========================
                                    INFORMATION GRID
                                ======================== */}

                <dl
                  className="
                                        m-0
                                        grid
                                        grid-cols-1
                                        gap-3
                                        md:grid-cols-2
                                        xl:grid-cols-3
                                    "
                >
                  {rows.map(([label, value]) => (
                    <div
                      key={label}
                      className="
                                                    min-w-0
                                                    rounded-xl
                                                    border
                                                    border-slate-100
                                                    bg-slate-50
                                                    p-3.5
                                                "
                    >
                      <dt
                        className="
                                                        flex
                                                        items-center
                                                        gap-1.5
                                                        text-[9px]
                                                        font-semibold
                                                        uppercase
                                                        tracking-[0.04em]
                                                        text-slate-400
                                                    "
                      >
                        <DetailIcon label={label} />

                        {label}
                      </dt>

                      <dd
                        className="
                                                        mb-0
                                                        mt-2
                                                        break-words
                                                        text-[12px]
                                                        font-semibold
                                                        leading-5
                                                        text-slate-900
                                                    "
                      >
                        {value}
                      </dd>
                    </div>
                  ))}
                </dl>

                {/* ========================
                                    ACTIONS
                                ======================== */}

                <div
                  className="
                                        mt-5
                                        flex
                                        flex-wrap
                                        items-center
                                        justify-between
                                        gap-3
                                        border-t
                                        border-slate-100
                                        pt-4

                                        max-sm:flex-col
                                        max-sm:items-stretch
                                    "
                >
                  <div>
                    <strong
                      className="
                                                block
                                                text-[11px]
                                                font-semibold
                                                text-slate-800
                                            "
                    >
                      Account actions
                    </strong>

                    <span
                      className="
                                                mt-0.5
                                                block
                                                text-[9px]
                                                text-slate-400
                                            "
                    >
                      Changes may affect active student sessions.
                    </span>
                  </div>

                  <div
                    className="
                                            flex
                                            flex-wrap
                                            items-center
                                            justify-end
                                            gap-2

                                            max-sm:grid
                                            max-sm:grid-cols-2
                                        "
                  >
                    {/* ENABLE */}

                    {activatedAccountButton}

                    {account.status === "disabled" && (
                      <button
                        type="button"
                        onClick={() => setAction("enable")}
                        className="
                                                    inline-flex
                                                    h-9
                                                    items-center
                                                    justify-center
                                                    gap-1.5
                                                    rounded-[9px]
                                                    border
                                                    border-emerald-200
                                                    bg-white
                                                    px-3
                                                    text-[10px]
                                                    font-semibold
                                                    text-emerald-700
                                                    transition
                                                    hover:bg-emerald-50
                                                "
                      >
                        <ShieldCheck size={14} />
                        Enable
                      </button>
                    )}

                    {/* DISABLE */}

                    {account.status !== "disabled" && (
                      <button
                        type="button"
                        onClick={() => setAction("disable")}
                        className="
                                                    inline-flex
                                                    h-9
                                                    items-center
                                                    justify-center
                                                    gap-1.5
                                                    rounded-[9px]
                                                    border
                                                    border-red-200
                                                    bg-white
                                                    px-3
                                                    text-[10px]
                                                    font-semibold
                                                    text-red-600
                                                    transition
                                                    hover:bg-red-50
                                                "
                      >
                        <ShieldOff size={14} />
                        Disable
                      </button>
                    )}

                    {/* LOGOUT */}

                    <button
                      type="button"
                      onClick={() => setAction("logout")}
                      className="
                                                inline-flex
                                                h-9
                                                items-center
                                                justify-center
                                                gap-1.5
                                                rounded-[9px]
                                                border
                                                border-slate-200
                                                bg-white
                                                px-3
                                                text-[10px]
                                                font-semibold
                                                text-slate-600
                                                transition
                                                hover:border-slate-300
                                                hover:bg-slate-50
                                                hover:text-slate-900
                                            "
                    >
                      <LogOut size={14} />
                      Log out all
                    </button>

                    {/* RESET */}

                    <button
                      type="button"
                      onClick={() => setAction("reset")}
                      className="
                                                inline-flex
                                                h-9
                                                items-center
                                                justify-center
                                                gap-1.5
                                                rounded-[9px]
                                                border
                                                border-amber-200
                                                bg-white
                                                px-3
                                                text-[10px]
                                                font-semibold
                                                text-amber-700
                                                transition
                                                hover:bg-amber-50
                                            "
                    >
                      <RefreshCcw size={14} />
                      Reset activation
                    </button>

                    {/* DELETE */}

                    <button
                      type="button"
                      onClick={() => setAction("delete")}
                      className="
                                                inline-flex
                                                h-9
                                                items-center
                                                justify-center
                                                gap-1.5
                                                rounded-[9px]
                                                border
                                                border-red-300
                                                bg-red-50
                                                px-3
                                                text-[10px]
                                                font-semibold
                                                text-red-700
                                                transition
                                                hover:bg-red-100
                                            "
                    >
                      <Trash2 size={14} />
                      Delete mobile account
                    </button>
                  </div>
                </div>
              </>
            )}
          </AsyncState>
        )}

        {/* ========================================
                    MUTATION ERROR
                ======================================== */}

        {mutationError && (
          <div
            role="alert"
            className="
                            mt-4
                            flex
                            items-start
                            gap-2.5
                            rounded-[10px]
                            border
                            border-red-200
                            bg-red-50
                            px-3
                            py-2.5
                            text-red-700
                        "
          >
            <TriangleAlert
              size={15}
              className="
                                mt-0.5
                                shrink-0
                            "
            />

            <div
              className="
                                min-w-0
                            "
            >
              <strong
                className="
                                    block
                                    text-[10px]
                                    font-semibold
                                "
              >
                Account action failed
              </strong>

              <span
                className="
                                    mt-0.5
                                    block
                                    text-[10px]
                                    leading-4
                                "
              >
                {mutationError.message}
              </span>

              {mutationError.requestId && (
                <code
                  className="
                                        mt-1
                                        block
                                        break-all
                                        text-[9px]
                                        text-red-500
                                    "
                >
                  Request ID: {mutationError.requestId}
                </code>
              )}
            </div>
          </div>
        )}

        {/* ========================================
                    ACTIVATION TOKEN
                ======================================== */}

        {activation && (
          <div
            role="status"
            className="
                            mt-5
                            overflow-hidden
                            rounded-xl
                            border
                            border-amber-200
                            bg-amber-50/50
                        "
          >
            {/* Token header */}

            <div
              className="
                                flex
                                items-start
                                gap-3
                                border-b
                                border-amber-100
                                px-4
                                py-3.5
                            "
            >
              <div
                className="
                                    grid
                                    h-8
                                    w-8
                                    shrink-0
                                    place-items-center
                                    rounded-lg
                                    bg-amber-100
                                    text-amber-700
                                "
              >
                <KeyRound size={15} />
              </div>

              <div>
                <strong
                  className="
                                        block
                                        text-[12px]
                                        font-semibold
                                        text-amber-950
                                    "
                >
                  One-time activation OTP
                </strong>

                <p
                  className="
                                        mb-0
                                        mt-1
                                        text-[10px]
                                        leading-4
                                        text-amber-800
                                    "
                >
                  {activation.activationEmailSent === true
                    ? "The login email and this OTP were sent to the student's email address. It will not be shown again after dismissal."
                    : activation.activationEmailSent === false
                      ? "The account was created, but the email could not be sent. Copy this OTP and share it only through the approved support channel."
                      : "Share this OTP only through the approved support channel. It will not be shown again after dismissal."}
                </p>
              </div>
            </div>

            {/* Token body */}

            <div
              className="
                                p-4
                            "
            >
              <div
                className="
                                    rounded-[10px]
                                    border
                                    border-amber-200
                                    bg-white
                                    p-3
                                "
              >
                <div
                  className="
                                        mb-1.5
                                        text-[9px]
                                        font-semibold
                                        uppercase
                                        tracking-[0.04em]
                                        text-slate-400
                                    "
                >
                  Activation OTP
                </div>

                <code
                  className="
                                        block
                                        break-all
                                        font-mono
                                        text-[12px]
                                        font-semibold
                                        leading-5
                                        text-slate-900
                                    "
                >
                  {activation.activationToken}
                </code>
              </div>

              <div
                className="
                                    mt-2
                                    flex
                                    items-center
                                    gap-1.5
                                    text-[9px]
                                    text-amber-700
                                "
              >
                <Clock3 size={12} />
                Expires {dates(activation.activationExpiresAt)}
              </div>

              {/* Buttons */}

              <div
                className="
                                    mt-4
                                    flex
                                    flex-wrap
                                    justify-end
                                    gap-2

                                    max-sm:grid
                                    max-sm:grid-cols-1
                                "
              >
                <button
                  type="button"
                  onClick={copyToken}
                  className="
                                        inline-flex
                                        h-9
                                        items-center
                                        justify-center
                                        gap-1.5
                                        rounded-[9px]
                                        border-0
                                        bg-[#0d162a]
                                        px-3
                                        text-[10px]
                                        font-semibold
                                        text-white
                                        transition
                                        hover:bg-[#172239]
                                    "
                >
                  {copied ? (
                    <>
                      <ClipboardCheck size={14} />
                      Copied
                    </>
                  ) : (
                    <>
                      <Clipboard size={14} />
                      Copy token
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setActivation(null);

                    setCopied(false);
                  }}
                  className="
                                        h-9
                                        rounded-[9px]
                                        border
                                        border-slate-200
                                        bg-white
                                        px-3
                                        text-[10px]
                                        font-semibold
                                        text-slate-600
                                        transition
                                        hover:bg-slate-50
                                    "
                >
                  Dismiss permanently
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ============================================
                CONFIRMATION
            ============================================ */}

      {action && (
        <ConfirmationDialog
          open
          title={actionCopy[action].title}
          description={actionCopy[action].description}
          confirmLabel={actionCopy[action].label}
          confirmationText={actionCopy[action].confirmation}
          requireReason
          reasonMinLength={actionCopy[action].reasonMinLength}
          danger={actionCopy[action].danger}
          busy={mutation.isPending}
          onCancel={() => setAction(null)}
          onConfirm={confirm}
        />
      )}
    </section>
  );
}
